import React from 'react'
import { CircularProgress, InputAdornment, MenuItem, TextField, TextFieldProps } from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { optionsToEntries } from './utils'
import type { LoadingProps, OptionsProps } from './types'

type Props<FormValues extends FieldValues = FieldValues> = UseControllerProps<FormValues> &
    TextFieldProps &
    OptionsProps &
    LoadingProps & {
        native?: boolean
    }

const LoadingInputAdornment: React.VFC = () => (
    <InputAdornment position="end">
        <CircularProgress size={20} sx={{ mr: '20px' }} />
    </InputAdornment>
)

export const MuiRhfSelectFieldInputRef = <FormValues extends FieldValues = FieldValues>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
    options = [],
    loading,
    native,
    ...textFieldProps
}: Props<FormValues>): JSX.Element => {
    const {
        field: { ref, value: fieldValue, ...rhfFieldProps },
        fieldState: { error },
    } = useController<FormValues>({ rules, shouldUnregister, defaultValue, name })

    const value = textFieldProps.value || fieldValue === undefined ? '' : fieldValue
    const optionEntries = optionsToEntries(loading && !options.length && value ? [value] : options)
    const errorMessage = error instanceof Error ? error.message : undefined

    return (
        <TextField
            inputRef={ref}
            {...rhfFieldProps}
            {...textFieldProps}
            onChange={(e) => {
                rhfFieldProps.onChange(e)
                textFieldProps.onChange?.(e)
            }}
            onBlur={(e) => {
                rhfFieldProps.onBlur()
                textFieldProps.onBlur?.(e)
            }}
            InputProps={{
                endAdornment: loading && <LoadingInputAdornment />,
                ...textFieldProps.InputProps,
            }}
            select
            value={textFieldProps.value || value === undefined ? '' : value}
            required={textFieldProps.required || !!rules?.required}
            error={textFieldProps.error || !!error}
            helperText={textFieldProps.helperText || errorMessage}
            SelectProps={{ native }}
            disabled={textFieldProps.disabled || loading}
        >
            {optionEntries.map(([optionValue, text, optionProps]) =>
                native ? (
                    optionValue ? (
                        <option
                            key={String(optionValue)}
                            // @ts-expect-error allow boolean optionValue
                            value={optionValue}
                            {...optionProps}
                        >
                            {String(text)}
                        </option>
                    ) : (
                        <option
                            key={String(optionValue)}
                            // @ts-expect-error allow boolean optionValue
                            value={optionValue}
                            {...optionProps}
                        />
                    )
                ) : (
                    // @ts-expect-error allow boolean optionValue
                    <MenuItem key={String(optionValue)} value={optionValue} {...optionProps}>
                        {String(text)}
                    </MenuItem>
                ),
            )}
        </TextField>
    )
}

export default MuiRhfSelectFieldInputRef
export {
    MuiRhfSelectFieldInputRef as MuiRhfSelectField,
    MuiRhfSelectFieldInputRef as MuiRhfSelect,
    MuiRhfSelectFieldInputRef as SelectField,
    MuiRhfSelectFieldInputRef as Select,
}
export type { Props as MuiRhfSelectFieldProps, Props as SelectFieldProps }
